import { useState, useEffect, useContext } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { VaultNavigationTabs } from 'common/enums';
import { DashboardScope } from 'context/vault-detail-context/context-reducer';
import { rememberLastVaultId } from 'helpers/vault-breadcrumbs';
import { getRouteFor } from 'routes/route-helpers';
import { RouteName } from 'routes';
import { VaultDetailContext } from 'context/vault-detail-context/context';
import { vaultDetailActions } from 'context/vault-detail-context/action-types';

export const useVaultDetailNavigation = () => {
  const { id: urlVaultId } = useParams<{ id: string }>();
  const history = useHistory();
  const {
    state: { vaultId },
    dispatch,
  } = useContext(VaultDetailContext);
  useEffect(() => {
    if (!urlVaultId) return;
    const parsedId = Number(urlVaultId) || 0;
    // Account-level screens' "Dashboard" crumb comes back here.
    rememberLastVaultId(parsedId);
    if (parsedId !== vaultId) {
      dispatch({
        type: vaultDetailActions.UPDATE_VAULT_ID,
        vaultId: parsedId,
      });
    }
  }, [urlVaultId, dispatch]);

  const getTabByQueryParam = () => {
    const pathParamTab = new URLSearchParams(window.location.search).get(
      'tab',
    ) as VaultNavigationTabs;
    if (Object.values(VaultNavigationTabs).includes(pathParamTab)) {
      return pathParamTab;
    }
    return VaultNavigationTabs.dashboard;
  };

  const location = useLocation();
  const [selectedTab, setSelectedTab] =
    useState<VaultNavigationTabs>(getTabByQueryParam());

  useEffect(() => {
    const newTab = getTabByQueryParam();
    if (newTab !== selectedTab) {
      setSelectedTab(newTab);
    }
  }, [location.search]);

  // `?scope=shared` re-selects the dashboard's "Shared With Me" scope. The
  // breadcrumb's Dashboard crumb uses it to bring the user back to where they
  // opened a shared album from (that album lives in another vault).
  useEffect(() => {
    const scopeParam = new URLSearchParams(location.search).get('scope');
    const scopes: DashboardScope[] = ['all', 'shared'];
    if (scopes.includes(scopeParam as DashboardScope)) {
      dispatch({
        type: vaultDetailActions.SET_DASHBOARD_SCOPE,
        scope: scopeParam as DashboardScope,
      });
    }
  }, [location.search, dispatch]);

  const changeTab = (
    newTab: VaultNavigationTabs,
    queryParams: ParamsType = {},
  ) => {
    if (!vaultId) return;
    if (newTab !== selectedTab) {
      const newUrl = getRouteFor(
        RouteName.VaultDetail,
        { id: vaultId },
        { ...queryParams, tab: newTab },
      );
      history.push(newUrl);
    }
  };

  // The route's vault id; the context copy lags a render behind it.
  const routeVaultId = Number(urlVaultId) || vaultId;

  return { selectedTab, changeTab, vaultId: routeVaultId };
};
